import { showToast } from "@/lib/utils/toast";
import {
  getPreferredFrameworkCode,
  toApiFrameworks,
} from "@/lib/utils/frameworks.utils";
import { sanitizeForFilename } from "@/lib/utils/text";
import { JJ_GROUP_FRAMEWORK_CODE } from "@/lib/constants/frameworks.constants";
import { authSessionStore } from "@/features/auth/stores/auth-session.store";
import { dataEntryService } from "../services/data-entry.services";
import { disclosureStore } from "../stores/disclosure.store";
import { entryDataStore } from "../stores/entry-data.store";
import { entryUIStore } from "../stores/entry-ui.store";
import { loadDataEntries } from "./load.ops";

const resolveFrameworkParam = (framework) =>
  toApiFrameworks(framework)[0] || framework;

const getUploadErrorMessage = (error) =>
  error?.response?.message || error?.message || "Failed to upload entries";

const toSelectOptions = (values = []) =>
  values.map((value) => ({ key: String(value), label: String(value) }));

const normalizeYearBoundaryOptions = ({ years = [], yearBoundaries = [] }) => {
  if (yearBoundaries.length > 0) {
    return yearBoundaries.map((option) => ({
      key: String(option.key || option.year),
      year: option.year,
      period: option.period || null,
    }));
  }
  return years.map((year) => ({
    key: String(year),
    year,
    period: null,
  }));
};

const formatReportingPeriod = (period) => {
  if (!period) return null;
  const { startMonth, startYear, endMonth, endYear } = period;
  if (!startMonth || !startYear || !endMonth || !endYear) return null;
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const startMonthName = monthNames[startMonth - 1] || `M${startMonth}`;
  const endMonthName = monthNames[endMonth - 1] || `M${endMonth}`;
  return `${startMonthName} ${startYear} - ${endMonthName} ${endYear}`;
};

const formatYearWithPeriod = (year, period) => {
  const formattedPeriod = formatReportingPeriod(period);
  return formattedPeriod ? `${year} (${formattedPeriod})` : String(year);
};

const yearBoundariesToSelectOptions = (yearBoundaries = []) =>
  yearBoundaries.map(({ key, year, period }) => ({
    key: String(key || year),
    label: formatYearWithPeriod(year, period),
  }));

const getSelectedReportingPeriodOption = () => {
  const selectedKey = String(entryUIStore.batchTemplateYear || "");
  if (!selectedKey) return null;
  return (
    batchTemplateYearCache.yearOptions.find(
      (option) => String(option.key || option.year) === selectedKey,
    ) || null
  );
};

const getSelectedReportingPeriodParams = () => {
  const selected = getSelectedReportingPeriodOption();
  const period = selected?.period;
  if (!period) {
    return selected?.year ? { year: Number(selected.year) } : {};
  }
  return {
    year: Number(selected.year || period.startYear),
    startYear: Number(period.startYear),
    startMonth: Number(period.startMonth),
    endYear: Number(period.endYear),
    endMonth: Number(period.endMonth),
  };
};

const getBatchTemplateDefaultFramework = () =>
  authSessionStore.isJJSEA || authSessionStore.user?.isJJSEA
    ? JJ_GROUP_FRAMEWORK_CODE
    : "";

const findOrgName = (nodes = [], orgId) => {
  const targetId = String(orgId || "");
  if (!targetId) return "";

  for (const node of nodes || []) {
    if (String(node?.id || "") === targetId) return node.name || "";
    const childMatch = findOrgName(node?.children || [], targetId);
    if (childMatch) return childMatch;
  }

  return "";
};

const findFrameworkName = (frameworks = [], frameworkCode) => {
  const framework = frameworks.find((f) => f.code === frameworkCode);
  return framework?.name || frameworkCode;
};

const buildBatchTemplateFilename = ({
  organizationId,
  year,
  framework,
  category,
}) => {
  const organizationName =
    findOrgName(entryDataStore.filteredOrgTree, organizationId) ||
    organizationId;
  const frameworkName = findFrameworkName(
    disclosureStore.frameworks,
    framework,
  );

  return `${[organizationName, year, frameworkName, category]
    .map(sanitizeForFilename)
    .join("_")}.xlsx`;
};

const buildUploadErrorResult = (parseResult, message) => ({
  ...parseResult,
  summary: {
    ...(parseResult?.summary || {}),
    totalValid: 0,
    totalErrors: 1,
    newRows: 0,
    updatedRows: 0,
    skippedRows: 0,
  },
  sheets: [
    {
      sheetName: "Batch Upload",
      totalRows: 1,
      validRows: 0,
      entries: [],
      errors: [
        {
          row: 0,
          field: "Confirm",
          message,
        },
      ],
    },
  ],
});

let batchTemplateFilterOptionsRequestId = 0;
let batchTemplateYearCache = {
  templateType: null,
  yearOptions: [],
};

export const downloadBatchUploadTemplate = async () => {
  const {
    setDownloadingTemplate,
    batchTemplateType,
    batchTemplateOrganizationId,
    batchTemplateYear,
    batchTemplateCategory,
    batchTemplateFramework,
  } = entryUIStore;

  if (
    !batchTemplateOrganizationId ||
    !batchTemplateYear ||
    !batchTemplateCategory ||
    !batchTemplateFramework
  ) {
    showToast.error(
      "Please select Reporting Period, Organization, Framework, and Category",
    );
    return;
  }

  setDownloadingTemplate(true);
  try {
    const selectedPeriodOption = getSelectedReportingPeriodOption();
    const selectedPeriodLabel = selectedPeriodOption
      ? formatYearWithPeriod(
          selectedPeriodOption.year,
          selectedPeriodOption.period,
        )
      : batchTemplateYear;

    await dataEntryService.downloadBatchUploadTemplate({
      framework: resolveFrameworkParam(batchTemplateFramework),
      templateType: batchTemplateType,
      organizationId: batchTemplateOrganizationId,
      ...getSelectedReportingPeriodParams(),
      category: batchTemplateCategory,
      downloadFilename: buildBatchTemplateFilename({
        organizationId: batchTemplateOrganizationId,
        year: selectedPeriodLabel,
        framework: batchTemplateFramework,
        category: batchTemplateCategory,
      }),
    });
  } catch (error) {
    showToast.error(error?.message || "Failed to download template");
  } finally {
    setDownloadingTemplate(false);
  }
};

// Reloads Reporting Period/Organization/Framework/Category options from the
// current top-down selection. Reporting periods only reload when template type
// changes.
export const loadBatchTemplateFilterOptions = async () => {
  const requestId = ++batchTemplateFilterOptionsRequestId;
  const {
    batchTemplateType,
    batchTemplateOrganizationId,
    batchTemplateYear,
    batchTemplateFramework,
    batchTemplateCategory,
    setLoadingBatchTemplateYears,
    setLoadingBatchTemplateFrameworks,
    setLoadingBatchTemplateCategories,
    setBatchTemplateYear,
    setBatchTemplateYearOptions,
    setBatchTemplateOrganizationOptions,
    setBatchTemplateFrameworkOptions,
    setBatchTemplateCategoryOptions,
  } = entryUIStore;

  if (!batchTemplateYear) {
    setBatchTemplateOrganizationOptions([]);
    setBatchTemplateFrameworkOptions([]);
    setBatchTemplateCategoryOptions([]);
    setLoadingBatchTemplateFrameworks(false);
    setLoadingBatchTemplateCategories(false);
  }

  const yearOptionScopeChanged =
    batchTemplateYearCache.templateType !== batchTemplateType;

  if (yearOptionScopeChanged) {
    setLoadingBatchTemplateYears(true);
  }
  if (batchTemplateYear && batchTemplateOrganizationId) {
    setLoadingBatchTemplateFrameworks(true);
    setLoadingBatchTemplateCategories(true);
  }

  try {
    const effectiveFramework =
      batchTemplateFramework || getBatchTemplateDefaultFramework();
    const result = await dataEntryService.getBatchUploadFilterOptions({
      templateType: batchTemplateType,
      organizationId: batchTemplateOrganizationId || undefined,
      ...getSelectedReportingPeriodParams(),
      framework:
        batchTemplateYear && batchTemplateOrganizationId
          ? effectiveFramework || undefined
          : undefined,
      category: batchTemplateCategory || undefined,
    });

    if (requestId !== batchTemplateFilterOptionsRequestId) return null;

    const years = Array.isArray(result?.years) ? result.years : [];
    const reportingPeriods = Array.isArray(result?.reportingPeriods)
      ? result.reportingPeriods
      : [];
    const yearOptions = normalizeYearBoundaryOptions({
      years,
      yearBoundaries: reportingPeriods.length
        ? reportingPeriods
        : Array.isArray(result?.yearBoundaries)
          ? result.yearBoundaries
          : [],
    });
    const organizations = Array.isArray(result?.organizations)
      ? result.organizations
      : [];
    const frameworks = Array.isArray(result?.frameworks)
      ? result.frameworks
      : [];
    const categories = Array.isArray(result?.categories)
      ? result.categories
      : [];

    // Cache reporting periods only when the top-level period scope changes.
    if (yearOptionScopeChanged) {
      batchTemplateYearCache.templateType = batchTemplateType;
      batchTemplateYearCache.yearOptions = yearOptions;
      setBatchTemplateYearOptions(yearBoundariesToSelectOptions(yearOptions));
    } else {
      // Reuse cached year options to keep them independent of framework selection
      setBatchTemplateYearOptions(
        yearBoundariesToSelectOptions(batchTemplateYearCache.yearOptions),
      );
    }

    if (
      batchTemplateYear &&
      !batchTemplateYearCache.yearOptions.some(
        (yb) => String(yb.key || yb.year) === String(batchTemplateYear),
      )
    ) {
      setBatchTemplateYear("");
      setBatchTemplateOrganizationOptions([]);
      setBatchTemplateFrameworkOptions([]);
      setBatchTemplateCategoryOptions([]);
      return result;
    }

    setBatchTemplateOrganizationOptions(organizations.map(String));
    setBatchTemplateFrameworkOptions(toSelectOptions(frameworks));
    setBatchTemplateCategoryOptions(toSelectOptions(categories));

    const defaultFramework = getPreferredFrameworkCode(
      frameworks,
      result?.defaultFramework || effectiveFramework,
    );
    if (!entryUIStore.batchTemplateFramework && defaultFramework) {
      entryUIStore.setBatchTemplateFramework(defaultFramework);
    }

    return result;
  } catch (error) {
    if (requestId !== batchTemplateFilterOptionsRequestId) return null;

    if (yearOptionScopeChanged) {
      batchTemplateYearCache = {
        templateType: null,
        yearOptions: [],
      };
      setBatchTemplateYearOptions([]);
    }
    setBatchTemplateOrganizationOptions([]);
    setBatchTemplateFrameworkOptions([]);
    setBatchTemplateCategoryOptions([]);
    showToast.error(error?.message || "Failed to load filter options");
    return null;
  } finally {
    if (requestId === batchTemplateFilterOptionsRequestId) {
      if (yearOptionScopeChanged) {
        setLoadingBatchTemplateYears(false);
      }
      setLoadingBatchTemplateFrameworks(false);
      setLoadingBatchTemplateCategories(false);
    }
  }
};

export const parseBatchUploadFile = async (file) => {
  const { setParsingFile, setParseResult } = entryUIStore;
  const resetVersion = entryUIStore.batchUploadResetVersion;

  setParsingFile(true);
  try {
    const data = await dataEntryService.parseBatchUpload(file);
    if (
      !entryUIStore.batchUploadOpen ||
      resetVersion !== entryUIStore.batchUploadResetVersion
    ) {
      return null;
    }
    setParseResult(data);

    const validation = data?.uploadValidation;
    if (validation?.code === "DUPLICATE_FILE" && validation?.message) {
      showToast.error(validation.message);
    }
    if (validation?.code === "EMPTY_UPLOAD" && validation?.message) {
      showToast.error(validation.message);
    }

    return data;
  } catch (error) {
    if (
      !entryUIStore.batchUploadOpen ||
      resetVersion !== entryUIStore.batchUploadResetVersion
    ) {
      return null;
    }
    showToast.error(error?.message || "Failed to parse uploaded file");
    return null;
  } finally {
    if (resetVersion === entryUIStore.batchUploadResetVersion) {
      setParsingFile(false);
    }
  }
};

export const confirmBatchUpload = async () => {
  const {
    parseResult,
    setConfirmingUpload,
    setBatchUploadOpen,
    setParseResult,
  } = entryUIStore;

  if (!parseResult) return;

  const validation = parseResult.uploadValidation;
  if (validation?.canConfirm === false) {
    showToast.error(validation?.message || "No valid entries to upload");
    return;
  }

  const allEntries = parseResult.sheets.flatMap((s) => s.entries || []);
  if (allEntries.length === 0) {
    showToast.error("No valid entries to upload");
    return;
  }

  setConfirmingUpload(true);
  try {
    const uploaded = await dataEntryService.confirmBatchUpload(allEntries);
    const uploadedCount = Array.isArray(uploaded)
      ? uploaded.length
      : allEntries.length;
    showToast.success(`${uploadedCount} entries uploaded successfully`);
    setBatchUploadOpen(false);
    if (disclosureStore.activeDisclosure?._id) {
      loadDataEntries();
    }
  } catch (error) {
    const message = getUploadErrorMessage(error);
    showToast.error(message);
    setParseResult(buildUploadErrorResult(parseResult, message));
  } finally {
    setConfirmingUpload(false);
  }
};
